/**
 * Mock backend client (Phase 1).
 *
 *   GET  /api/games/feed  -> GameMeta[]
 *   POST /api/telemetry   -> 202
 *
 * In `vite dev` / `vite preview` these are served by tools/dev-api-plugin.ts; on a static
 * host the feed is a redirect to /api/games/feed.json (emitted at build) and telemetry POSTs
 * are absorbed by the service worker. If the network path fails for any reason we fall back
 * to the bundled MOCK_GAMES so the carousel always has something to show.
 */
import type { GameMeta } from '../lib/protocol';
import type { MechanicalTelemetry } from '../telemetry/TelemetryTypes';
import { supabase } from '../backend/supabase';
import { cardRowToMeta, type CardRow } from './cards';
import { MOCK_GAMES } from './mockGames';

export const FEED_URL = '/api/games/feed';
export const TELEMETRY_URL = '/api/telemetry';

export interface FeedResult {
  games: GameMeta[];
  /** database = the card registry via feed(); network = the static JSON; bundled = MOCK_GAMES. */
  source: 'database' | 'network' | 'bundled';
}

/**
 * The feed. The database (public.feed(), ordered server-side) is the source of truth when the
 * build has a backend; the static JSON is the same catalogue without the ordering or anything
 * anyone has linked since the build, and is what the e2e suite runs against.
 */
/**
 * This page load's seed for feed(n, seed) (migration 20260929130000): the server keeps the head
 * as card_rank placed it and shuffles the tail by this seed, so every visit opens on a
 * different deck while a refresh within the visit keeps the order it has.
 */
export const FEED_SEED = Math.random().toString(36).slice(2, 10) || 'seed';

export async function fetchFeed(signal?: AbortSignal): Promise<FeedResult> {
  if (supabase) {
    try {
      const { data, error } = await supabase.rpc('feed', { n: 60, seed: FEED_SEED });
      if (!error && Array.isArray(data) && data.length) {
        return { games: (data as CardRow[]).map(cardRowToMeta), source: 'database' };
      }
    } catch {
      /* the static feed below is the fallback, exactly as for a failed network fetch */
    }
  }
  try {
    const res = await fetch(FEED_URL, { signal, headers: { accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = (await res.json()) as unknown;
    const games = Array.isArray(json) ? (json as GameMeta[]) : ((json as { games?: GameMeta[] }).games ?? []);
    if (!games.length) throw new Error('empty feed');
    return { games, source: 'network' };
  } catch {
    return { games: MOCK_GAMES, source: 'bundled' };
  }
}

/**
 * A shared card the static feed does not carry, from the bundled catalogue — the no-backend twin
 * of card_by_slug. Only used when the feed itself did not come from the database: in production
 * the catalogue cards are hidden, and a share link must not bring one back from the bundle.
 */
export function bySlug(slug: string): GameMeta | null {
  return MOCK_GAMES.find((g) => g.id === slug) ?? null;
}

export async function postTelemetry(batches: MechanicalTelemetry[], keepalive = false): Promise<boolean> {
  try {
    const res = await fetch(TELEMETRY_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(batches),
      keepalive,
    });
    return res.status >= 200 && res.status < 300;
  } catch {
    return false;
  }
}
